<template>
<div>
  <div v-if="!appConfig.CUSTOM_MACROS">
    <h2>
      <span>We have everything we need!</span>
    </h2>

    <p class="macro-final-step">
      Click "Finish" to check out your ideal balance of macros to meet your current goals. Check in periodically to assess if you need to make adjustments as
      your goals, fitness status, or weight may change.
    </p>
  </div>

  <div v-else>
    <h1>Let's fine-tune your goals.</h1>

    <baseInputWithLabel
      :class="{ 'not-valid-data': (() => ($v.form.targetWeight.$dirty && $v.form.targetWeight.$invalid ? true : undefined))() }"
      label="Target Weight (lbs)"
      inputType="text"
      v-model="form.targetWeight"
      :required="true"
      @baseInputChanged="
        ($event) => {
          setMacrosTargetWeight($event.target.value);
          form.targetWeight = $event.target.value;
          $v.form.targetWeight.$touch();
        }
      "
    />

    <baseInputWithLabel
      :class="{ 'not-valid-data': (() => ($v.form.calories.$dirty && $v.form.calories.$invalid ? true : undefined))() }"
      label="Current Calories (after tracking calories)"
      inputType="text"
      v-model="form.calories"
      :required="true"
      @baseInputChanged="
        ($event) => {
          setMacrosCalories($event.target.value);
          form.calories = $event.target.value;
          $v.form.calories.$touch();
        }
      "
    />

    <baseSelectWithLabel
      label="Body Type"
      inputType="text"
      :value="form.bodyType"
      :options="bodyTypeOptions"
      @baseSelectChanged="
        ($event) => {
          setMacrosBodyTypeValue($event.target.value);
          form.bodyType = $event.target.value;
          $v.form.bodyType.$touch();
        }
      "
    />
  </div>
</div>
</template>

<script>
import appConfig from '@/src/app.config.ES6';
import store from '@state/store';
import Vue from 'vue';
import { macroCalculatorComputed, macroCalculatorMethods } from '@state/helpers';
import { validationMixin } from 'vuelidate';
import { required, integer, numeric } from 'vuelidate/lib/validators';

export default {
  mixins: [validationMixin],
  components: {},
  props: ['clickedNext', 'currentStep'],
  validations: {
    form: {
      bodyType: {
        required,
      },
      calories: {
        required,
        integer,
      },
      targetWeight: {
        required,
        numeric,
      },
    },
  },
  data() {
    return {
      appConfig,
      form: {
        bodyType: null,
        calories: null,
        targetWeight: null,
      },
    };
  },
  watch: {
    $v: {
      handler: function(val) {
        if (this.appConfig.CUSTOM_MACROS) {
          if (!val.$invalid) {
            this.$emit('can-continue', { value: true });
          } else {
            this.$emit('can-continue', { value: false });
          }
        } else {
          this.$emit('can-continue', { value: true });
        }
      },
      deep: true,
    },
    clickedNext(val) {
      if (this.appConfig.CUSTOM_MACROS) {
        if (val === true) {
          this.$v.form.$touch();
        }
      }
    },
    currentStep() {
      // Final confirmation step so let's allow users to continue
      this.$emit('can-continue', { value: true });
    },
  },
  mounted() {
    if (this.appConfig.CUSTOM_MACROS) {
      if (!this.$v.$invalid) {
        this.$emit('can-continue', { value: true });
      } else {
        this.$emit('can-continue', { value: false });
      }
    } else {
      this.$emit('can-continue', { value: true });
      this.setMacrosFat(this.fatF());
      this.setMacrosProtein(this.proteinF());
      this.setMacrosCarbs(this.carbsF());
      // this.$emit('change-next', {nextBtnValue: });
    }
  },
  // Custom macros do not use calculation of Macro step four
  // For all formulas the weight must be used in kg, conversion is not necessary, use weightInKg instead.
  methods: {
    ...macroCalculatorMethods,
    BMR() {
      if (this.appConfig.HB_MACROS) {
        switch (this.genderSelectionIndex) {
          case 0: //male
            return 13.397 * this.weightInKg + 4.799 * this.heightInCm - 5.677 * this.age + 88.362;
          case 1: //female
            return 9.247 * this.weightInKg + 3.098 * this.heightInCm - 4.33 * this.age + 444.593;
        }
      } else if (this.appConfig.NATY_MACROS) {
        switch (this.genderSelectionIndex) {
          case 0: //male
            return 13.7 * this.weightInKg + 5 * this.heightInCm - 6.8 * this.age + 66;
          case 1: //female
            return 9.6 * this.weightInKg + 1.8 * this.heightInCm - 4.7 * this.age + 655;
        }
      } else if (this.appConfig.MIFFLIN_MACROS) {
        switch (this.genderSelectionIndex) {
          case 0: //male
            return 10 * this.weightInKg + 6.25 * this.heightInCm - 5 * this.age + 5;
          case 1: //female
            return 10 * this.weightInKg + 6.25 * this.heightInCm - 5 * this.age - 161;
        }
      } else {
        switch (this.genderSelectionIndex) {
          case 0: //male
            return 4.54 * this.weightInKg + 6.25 * this.heightInCm - 5 * this.age + 5;
          case 1: //female
            return 4.54 * this.weightInKg + 6.25 * this.heightInCm - 5 * this.age - 161;
        }
      }
    },
    // Standard values do not change
    TDEE() {
      switch (this.activityLevelSelectionIndex) {
        case 0: //sedentary
          return this.BMR() * 1.2;
        case 1: //lightly active
          return this.BMR() * 1.375;
        case 2: //moderately active
          return this.BMR() * 1.55;
        case 3: //very active
          return this.BMR() * 1.725;
        case 4: //extreme
          return this.BMR() * 1.9;
      }
    },
    goalTDEE() {
      if (this.appConfig.NATY_MACROS) {
        switch (this.goalSelectionIndex) {
          case 0: //Weight Loss
            return this.TDEE() - this.TDEE() * 0.25;
          case 1: //Maintain
            return this.TDEE();
          case 2: //Weight Gain
            return this.TDEE() + this.TDEE() * 0.2;
        }
      } else {
        switch (this.goalSelectionIndex) {
          case 0: //Weight Loss
            return this.TDEE() * 0.8;
          case 1: //Maintain
            return this.TDEE();
          case 2: //Weight Gain
            return this.TDEE() + this.TDEE() * 0.2;
        }
      }
    },
    proteinF() {
      if (this.appConfig.HB_MACROS || this.appConfig.MIFFLIN_MACROS) {
        switch (this.goalSelectionIndex) {
          case 0: //Weight Loss
            return Math.round((0.3 * this.goalTDEE()) / 4);
          case 1: //Maintain
            return Math.round((0.25 * this.goalTDEE()) / 4);
          case 2: //Weight Gain
            return Math.round((0.25 * this.goalTDEE()) / 4);
        }
      } else if (this.appConfig.NATY_MACROS) {
        switch (this.goalSelectionIndex) {
          case 0: //Weight Loss
            return Math.round((0.3 * this.goalTDEE()) / 4);
          case 1: //Maintain
            return Math.round((0.3 * this.goalTDEE()) / 4);
          case 2: //Weight Gain
            return Math.round((0.3 * this.goalTDEE()) / 4);
        }
      } else {
        return Math.round(0.825 * this.weightInKg);
      }
    },
    proteinPercent() {
      return (this.proteinF() / this.macroTotal()) * 100;
    },
    fatF() {
      if (this.appConfig.HB_MACROS || this.appConfig.MIFFLIN_MACROS) {
        switch (this.goalSelectionIndex) {
          case 0: //Weight Loss
            return Math.round((0.3 * this.goalTDEE()) / 9);
          case 1: //Maintain
            return Math.round((0.3 * this.goalTDEE()) / 9);
          case 2: //Weight Gain
            return Math.round((0.25 * this.goalTDEE()) / 9);
        }
      } else if (this.appConfig.NATY_MACROS) {
        switch (this.goalSelectionIndex) {
          case 0: //Weight Loss
            return Math.round((0.3 * this.goalTDEE()) / 8);
          case 1: //Maintain
            return Math.round((0.3 * this.goalTDEE()) / 8);
          case 2: //Weight Gain
            return Math.round((0.25 * this.goalTDEE()) / 8);
        }
      } else {
        return Math.round((0.25 * this.goalTDEE()) / 9);
      }
    },
    fatPercent() {
      return (this.fatF() / this.macroTotal()) * 100;
    },
    carbsF() {
      if (this.appConfig.HB_MACROS || this.appConfig.MIFFLIN_MACROS) {
        switch (this.goalSelectionIndex) {
          case 0: //Weight Loss
            return Math.round((0.4 * this.goalTDEE()) / 4);
          case 1: //Maintain
            return Math.round((0.45 * this.goalTDEE()) / 4);
          case 2: //Weight Gain
            return Math.round((0.5 * this.goalTDEE()) / 4);
        }
      } else if (this.appConfig.NATY_MACROS) {
        switch (this.goalSelectionIndex) {
          case 0: //Weight Loss
            return Math.round((0.4 * this.goalTDEE()) / 4);
          case 1: //Maintain
            return Math.round((0.4 * this.goalTDEE()) / 4);
          case 2: //Weight Gain
            return Math.round((0.45 * this.goalTDEE()) / 4);
        }
      } else {
        return Math.round((this.goalTDEE() - this.proteinF() * 4 - this.fatF() * 9) / 6);
      }
    },
    carbsPercent() {
      return (this.carbsF() / this.macroTotal()) * 100;
    },
    macroTotal() {
      return this.proteinF() + this.fatF() + this.carbsF();
    },
  },
  computed: {
    ...macroCalculatorComputed,
  },
};
</script>

<style lang="scss">
@import 'theme.scss';

.cdc-container {
  margin-bottom: 2rem;
}

.button-container {
  margin-bottom: 2rem;

  label {
    font-size: 1.7rem;
    margin-bottom: 1rem;
  }
}

.macro-final-step {
  font-size: 1.7rem;
  line-height: 1.2;
  margin-bottom: 3rem;
  margin-top: 1.6rem;
}
</style>
